import {useId, useState} from 'react';

import {useSkeletonReveal} from '../../hooks';
import {cn} from '../../lib/utils';
import {Field, FieldSkeleton} from '../Field';
import {Icon} from '../Icon';
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInputShell,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxPortal,
  ComboboxPositioner,
  onComboboxUserInputChange
} from '../shadcn/combobox';

import type {FieldProps} from '../Field';
import type {FocusEventHandler, KeyboardEventHandler, ReactNode, Ref} from 'react';

export interface AddressSuggestion {
  placeId: string;
  mainText: string;
  secondaryText?: string;
}

export interface FieldAddressAutocompleteProps {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  orientation?: FieldProps['orientation'];
  required?: boolean;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  startAdornment?: ReactNode;
  cornerBadge?: ReactNode;
  isLoading?: boolean;
  /** Renders a lock glyph next to the label and disables the input. */
  isLocked?: boolean;
  /** Tooltip shown on the lock glyph explaining why the field can't be edited. */
  lockedTooltip?: ReactNode;
  ref?: Ref<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;

  inputValue: string;
  onInputValueChange: (text: string) => void;

  suggestions: AddressSuggestion[];
  onSelectSuggestion: (suggestion: AddressSuggestion) => void;
  loading?: boolean;
  errorText?: ReactNode;
  emptyText?: ReactNode;
}

export function FieldAddressAutocomplete({
  label,
  description,
  error,
  orientation,
  required,
  id,
  disabled,
  placeholder,
  startAdornment,
  cornerBadge,
  isLoading,
  isLocked,
  lockedTooltip,
  ref,
  onBlur,
  onFocus,
  onKeyDown,
  inputValue,
  onInputValueChange,
  suggestions,
  onSelectSuggestion,
  loading,
  errorText,
  emptyText
}: FieldAddressAutocompleteProps) {
  const generatedId = useId();
  const resolvedId = id ?? generatedId;
  const [wantOpen, setWantOpen] = useState(false);
  // Base UI opens the popup on focus even with nothing to show — gate on real content.
  const hasContent = suggestions.length > 0 || !!errorText || !!emptyText;
  const {showSkeleton, onFocus: onRevealFocus} = useSkeletonReveal(isLoading);
  const resolvedStartAdornment = startAdornment ?? <Icon name="marker-pin" className="size-4" />;
  const isDisabled = disabled || isLocked;

  return (
    <Field
      label={label}
      description={description}
      error={error}
      htmlFor={resolvedId}
      orientation={orientation}
      required={required}
      cornerBadge={cornerBadge}
      isLocked={isLocked}
      lockedTooltip={lockedTooltip}
    >
      <Combobox<AddressSuggestion>
        data-slot="field-address-autocomplete"
        items={suggestions}
        filter={null}
        inputValue={inputValue}
        onInputValueChange={onComboboxUserInputChange(onInputValueChange)}
        itemToStringLabel={item => item.mainText}
        value={null}
        onValueChange={item => {
          if (!item) return;
          onSelectSuggestion(item);
        }}
        open={wantOpen && hasContent}
        onOpenChange={setWantOpen}
        disabled={isDisabled}
      >
        <div className="relative">
          {showSkeleton && <FieldSkeleton className="top-1/2 inset-s-9 -translate-y-1/2" />}
          <ComboboxInputShell
            id={resolvedId}
            hasError={!!error}
            placeholder={placeholder}
            disabled={isDisabled}
            startAdornment={resolvedStartAdornment}
            loading={loading}
            inputRef={ref}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onFocus={e => {
              onRevealFocus();
              onFocus?.(e);
            }}
            className={showSkeleton ? 'placeholder:opacity-0' : undefined}
          />
        </div>
        <ComboboxPortal>
          <ComboboxPositioner collisionAvoidance={{side: 'none'}}>
            <ComboboxPopup>
              <ComboboxList>
                {suggestions.map(suggestion => (
                  <ComboboxItem key={suggestion.placeId} value={suggestion}>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{suggestion.mainText}</span>
                      {suggestion.secondaryText && (
                        <span className="truncate text-xs text-fg-tertiary">
                          {suggestion.secondaryText}
                        </span>
                      )}
                    </div>
                  </ComboboxItem>
                ))}
                <ComboboxEmpty>
                  {!loading && (errorText || emptyText) && (
                    <span
                      className={cn(
                        'block px-3 py-2 text-start text-sm whitespace-normal',
                        errorText ? 'text-fg-error' : 'text-fg-tertiary'
                      )}
                    >
                      {errorText ?? emptyText}
                    </span>
                  )}
                </ComboboxEmpty>
              </ComboboxList>
            </ComboboxPopup>
          </ComboboxPositioner>
        </ComboboxPortal>
      </Combobox>
    </Field>
  );
}
